import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, KeyRound, ShieldQuestion } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button-variants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AuthShell } from './AuthShell';

/**
 * `/forgot-password` — and what it deliberately is NOT.
 *
 * THERE IS NO PASSWORD RESET IN THIS PRODUCT. `auth.controller.ts` carries five
 * routes — `login`, `login/pin`, `refresh`, `logout`, `me` — and no sixth. There
 * is no reset-token model. There is no mail transport anywhere in the backend:
 * `EmailNotificationChannel` extends `UnimplementedChannel`, whose `send()`
 * persists the row and returns `PENDING` with
 * `"EMAIL adapter not implemented — row persisted, nothing sent"`, and the API's
 * package.json has no mailer dependency at all.
 *
 * So the obvious build — an email box and a "check your inbox" confirmation —
 * would be a lie with a very long tail: the person waits, then checks spam, then
 * waits again, and the reset they are waiting for does not exist and never will
 * arrive. There is no version of that screen that is not worse than telling the
 * truth.
 *
 * What this page is instead: the true state of affairs, and the route that
 * actually works. `PUT /api/users/:id/password` takes `newPassword` alone when
 * the actor is not the subject, gated on `users.update` — held by TENANT_OWNER
 * and TENANT_ADMIN, and NOT by BRANCH_MANAGER, so the copy says "owner or admin"
 * rather than "your manager". The Dashboard already exposes it at
 * Settings → Staff → the key icon → `StaffCredentialsDialog`, so the instructions
 * name a screen that exists today rather than an API call.
 *
 * The one case with no remedy is stated rather than glossed: a sole owner, with
 * nobody else holding those keys, cannot recover their own password and has to
 * reach support. Saying so is what stops that person spending an evening looking
 * for a reset link.
 *
 * A page and not a panel on `/login`: the login screen is two fields and stays
 * that way, and the honest answer is four paragraphs. It also gives the link
 * somewhere to go, which is what was actually asked for.
 */
export default function ForgotPassword() {
  const { t } = useTranslation();

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">
            {t('auth.forgot.title')}
          </CardTitle>
          <CardDescription>
            {t('auth.forgot.lead')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <section className="flex gap-3">
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
            <div className="min-w-0 space-y-1">
              <h2 className="text-sm font-medium">
                {t('auth.forgot.staffHeading')}
              </h2>
              <p className="text-sm text-ink-muted">
                {t('auth.forgot.staffBody')}
              </p>
            </div>
          </section>

          <section className="flex gap-3">
            <ShieldQuestion className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
            <div className="min-w-0 space-y-1">
              <h2 className="text-sm font-medium">
                {t('auth.forgot.ownerHeading')}
              </h2>
              <p className="text-sm text-ink-muted">
                {t('auth.forgot.ownerBody')}
              </p>
            </div>
          </section>

          <p className="border-t border-line pt-4 text-xs text-ink-muted">
            {t('auth.forgot.pinNote')}
          </p>

          <Link to="/login" className={buttonVariants({ variant: 'outline', className: 'w-full' })}>
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
            {t('auth.forgot.back')}
          </Link>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
